import React, { useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Flex, Grid, Spinner, Text } from '@chakra-ui/react'
import { Icon } from '@ubirider/pick-component-library'
import { motion } from 'motion/react'
import { useTrips } from '../../../../context/DataProvider'

import BalanceIcon from '../../../../assets/dollar.svg'
import BoatIcon from '../../../../assets/boat.svg'
import { formatCurrency } from '../../../../helpers/currency'

const MFlex = motion.create(Flex)
const MGrid = motion.create(Grid)

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { when: 'beforeChildren', staggerChildren: 0.08, delayChildren: 0.05 },
  },
}

const cardVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 360, damping: 26 },
  },
}

export const InfoSection = ({ onEnterComplete }) => {
  const { t } = useTranslation()
  const { trips, loading } = useTrips()

  const amountSpent = useMemo(() => {
    if (!trips.length) return '0.00'
    const totalAmount = trips.reduce((sum, item) => {
      const n = Number(item?.amount)
      return sum + (Number.isFinite(n) ? Math.round(n * 100) : 0)
    }, 0)
    return formatCurrency(totalAmount)
  }, [trips])

  const firedRef = useRef(false)
  useEffect(() => {
    if (loading) firedRef.current = false
  }, [loading])
  const fireOnce = () => {
    if (firedRef.current) return
    firedRef.current = true
    onEnterComplete?.()
  }

  if (loading) {
    return (
      <Flex minH='240px' bg='neutral.100' align='center' justify='center' borderRadius='8px'>
        <Spinner thickness='6px' color='neutral.500' size='xl' />
      </Flex>
    )
  }

  return (
    <MFlex
      gap={{ sm: '24px', md: '16px', lg: '16px', xl: '16px' }}
      direction='column'
      width='100%'
      initial='hidden'
      animate='visible'
      variants={containerVariants}
      onAnimationComplete={fireOnce}
    >
      {/* Desktop / tablet */}
      <MFlex
        direction='column'
        overflow='hidden'
        align='center'
        display={{ sm: 'none', md: 'flex' }}
        variants={containerVariants}
      >
        <MGrid
          variants={containerVariants}
          templateColumns={{
            sm: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(2, 1fr)',
            xl: 'repeat(2, 1fr)',
            xxl: 'repeat(2, 1fr)',
          }}
          gap='20px'
          w='100%'
        >
          <MFlex
            direction='column'
            bg='#fff'
            border='1px solid #EAECF0'
            borderRadius='8px'
            padding='24px'
            gap='16px'
            variants={cardVariants}
          >
            <Flex bg='#F9FAFB' rounded='14px' w='48px' h='48px' align='center' justify='center'>
              <Icon src={BoatIcon} />
            </Flex>
            <Flex direction='column'>
              <Text fontSize='14px' fontWeight='600' color='neutral.500' textTransform='uppercase'>
                {t('acqua_vias.home_total_trips')}
              </Text>
              <Text fontSize='30px' fontWeight='700'>
                {trips.length}
              </Text>
            </Flex>
          </MFlex>

          <MFlex
            direction='column'
            bg='#fff'
            border='1px solid #EAECF0'
            borderRadius='8px'
            padding='24px'
            gap='16px'
            variants={cardVariants}
          >
            <Flex bg='#F9FAFB' rounded='14px' w='48px' h='48px' align='center' justify='center'>
              <Icon src={BalanceIcon} fill='#155DFC' />
            </Flex>
            <Flex direction='column'>
              <Text fontSize='14px' fontWeight='600' color='neutral.500' textTransform='uppercase'>
                {t('acqua_vias.home_amount_spent')}
              </Text>
              <Text fontSize='30px' fontWeight='700'>
                R$ {amountSpent}
              </Text>
            </Flex>
          </MFlex>
        </MGrid>
      </MFlex>

      {/* Mobile */}
      <MFlex
        direction='column'
        overflow='hidden'
        align='center'
        justify='space-between'
        mr='-8px'
        ml='-24px'
        display={{ sm: 'flex', md: 'none' }}
        variants={containerVariants}
      >
        <MGrid
          variants={containerVariants}
          templateColumns={{
            sm: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(2, 1fr)',
            xl: 'repeat(2, 1fr)',
            xxl: 'repeat(2, 1fr)',
          }}
          gap='16px'
          overflowX='scroll'
          w='100%'
          pb='16px'
          pl='16px'
          sx={{ '::-webkit-scrollbar': { display: 'none' } }}
        >
          <MFlex
            direction='column'
            bg='#fff'
            border='1px solid #EAECF0'
            borderRadius='8px'
            padding='24px'
            gap='16px'
            variants={cardVariants}
          >
            <Flex bg='#F9FAFB' rounded='14px' w='48px' h='48px' align='center' justify='center'>
              <Icon src={BoatIcon} />
            </Flex>
            <Flex direction='column'>
              <Text fontSize='14px' fontWeight='600' color='neutral.500' textTransform='uppercase'>
                {t('acqua_vias.home_total_trips')}
              </Text>
              <Text fontSize='30px' fontWeight='700'>
                {trips.length}
              </Text>
            </Flex>
          </MFlex>

          <MFlex
            direction='column'
            bg='#fff'
            border='1px solid #EAECF0'
            borderRadius='8px'
            padding='24px'
            gap='16px'
            variants={cardVariants}
          >
            <Flex bg='#F9FAFB' rounded='14px' w='48px' h='48px' align='center' justify='center'>
              <Icon src={BalanceIcon} fill='#155DFC' />
            </Flex>
            <Flex direction='column'>
              <Text fontSize='14px' fontWeight='600' color='neutral.500' textTransform='uppercase'>
                {t('acqua_vias.home_amount_spent')}
              </Text>
              <Text fontSize='30px' fontWeight='700'>
                R$ {amountSpent}
              </Text>
            </Flex>
          </MFlex>
        </MGrid>
      </MFlex>
    </MFlex>
  )
}
